import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, Truck, CreditCard, Phone, PartyPopper, FileDown, Copy, ExternalLink, Store, Package, Check } from "lucide-react";
import api, { API } from "@/lib/api";
import { money, STATUS_META, DELIVERY_COLOR } from "@/lib/orderStatus";
import { applyStoreLanguage } from "@/i18n";
import { saveMyOrder } from "@/lib/myOrders";
import CustomerAssistant from "@/components/CustomerAssistant";
import PaymentPanel from "@/components/PaymentPanel";
import PaymentSuccess from "@/components/PaymentSuccess";

function timelineFor(order, t) {
  if (order.payment_type === "on_pickup" || order.mode === "retrait") {
    return [
      { key: "a_valider", label: t("tracking.stReceived"), Icon: Clock },
      { key: "preparation", label: t("tracking.stPreparing"), Icon: Package },
      { key: "en_attente_retrait", label: t("tracking.stReadyPickup"), Icon: Store },
      { key: "livre", label: t("tracking.stPickedUp"), Icon: CheckCircle2 },
    ];
  }
  if (order.payment_type === "on_delivery") {
    return [
      { key: "a_valider", label: t("tracking.stReceived"), Icon: Clock },
      { key: "preparation", label: t("tracking.stPreparing"), Icon: Package },
      { key: "en_livraison", label: t("tracking.stDelivering"), Icon: Truck },
      { key: "livre", label: t("tracking.stDeliveredCod"), Icon: CheckCircle2 },
    ];
  }
  return [
    { key: "a_valider", label: order.paid ? t("tracking.stPaid") : t("tracking.stInvoice"), Icon: CreditCard },
    { key: "preparation", label: t("tracking.stPreparing"), Icon: Package },
    { key: "en_livraison", label: t("tracking.stDelivering"), Icon: Truck },
    { key: "livre", label: t("tracking.stDelivered"), Icon: CheckCircle2 },
  ];
}


export default function OrderTracking() {
  const { orderId } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: order, isLoading, isError } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => (await api.get(`/public/orders/${orderId}`)).data,
    refetchInterval: 10000,
  });

  useEffect(() => { if (order?.business_language) applyStoreLanguage(order.business_language); }, [order?.business_language]);

  const [showPaySuccess, setShowPaySuccess] = useState(false);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/public/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          qc.invalidateQueries({ queryKey: ["order", orderId] });
          window.history.replaceState(null, "", `/commande/${orderId}`);
          setShowPaySuccess(true);
          return;
        }
        if (data.status === "expired") { toast.error(t("tracking.sessionExpired")); return; }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Checkout status poll failed", err);
      }
      if (attempts < 6) setTimeout(poll, 2000);
    };
    poll();
  }, [orderId, qc, t]);

  useEffect(() => {
    if (order?.order_id) saveMyOrder({ orderId: order.order_id, slug: order.business_slug, storeName: order.business_name });
  }, [order?.order_id, order?.business_slug, order?.business_name]);

  if (isLoading) return <Center>{t("tracking.loading")}</Center>;
  if (isError || !order) return <Center>{t("tracking.notFound")}</Center>;

  const steps = timelineFor(order, t);
  const idxStatus = order.status === "a_payer" ? "a_valider" : (order.status === "remis_transporteur" ? "en_livraison" : order.status);
  const currentIdx = order.status === "annule" ? -1 : steps.findIndex((s) => s.key === idxStatus);
  const meta = STATUS_META[order.status];
  const currency = order.currency || "XOF";
  const dc = DELIVERY_COLOR[order.delivery_color] || DELIVERY_COLOR.green;

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <div className="max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto px-4 py-8 md:py-12">
        <div className="text-center mb-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E85D04]">{order.business_name}</p>
          <h1 className="font-display text-2xl font-extrabold mt-1">{t("tracking.title")}</h1>
          <p className="text-sm text-[#9c968a]">#{order.order_id.slice(-6)}</p>
          <span className={`inline-block mt-3 text-sm px-4 py-1.5 rounded-full font-semibold ${meta.cls}`} data-testid="order-status">{t(`status.${order.status}`)}</span>
          <div className="mt-3">
            <button
              data-testid="copy-tracking-link-btn"
              onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success(t("tracking.copied")); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E5E0D8] text-[#5C574F] text-xs font-semibold hover:text-[#E85D04] hover:border-[#E85D04] transition-colors"
            >
              <Copy className="h-3.5 w-3.5" /> {t("tracking.copyLink")}
            </button>
            <p className="text-[11px] text-[#9c968a] mt-1.5">{t("tracking.copyHint")}</p>
          </div>
        </div>

        {order.paid && order.status === "livre" && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#2D6A4F] text-white rounded-2xl p-5 text-center mb-5">
            <PartyPopper className="h-8 w-8 mx-auto mb-2" />
            <p className="font-display font-bold">{t("tracking.completed")}</p>
          </motion.div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 mb-5">
          {steps.map((s, i) => {
            const completed = i < currentIdx;
            const current = i === currentIdx;
            const done = completed || current;
            const Icon = s.Icon;
            return (
              <div key={s.key} className="flex items-start gap-3 pb-5 last:pb-0 relative">
                {i < steps.length - 1 && <span className={`absolute left-[18px] top-9 bottom-0 w-0.5 ${completed ? "bg-[#1B4332]" : "bg-[#E5E0D8]"}`} />}
                <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 z-10 transition-colors ${completed ? "bg-[#1B4332] text-white" : current ? "bg-[#E85D04] text-white ring-4 ring-[#E85D04]/20" : "bg-[#F9F6F0] text-[#cfc8ba]"}`} data-testid={`step-${s.key}-${completed ? "done" : current ? "current" : "todo"}`}>
                  {completed ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </span>
                <div className="pt-1.5">
                  <p className={`font-semibold text-sm ${done ? "text-[#1A1A1A]" : "text-[#9c968a]"}`}>{s.label}</p>
                  {current && <p className="text-[11px] text-[#E85D04] font-medium">{t("tracking.inProgress")}</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Order details */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 mb-5">
          <h3 className="font-display font-semibold mb-3">{t("tracking.details")}</h3>
          {order.items.map((it, i) => (
            <div key={`${it.product_id}-${it.size || ""}-${i}`} className="flex justify-between text-sm py-1"><span className="text-[#5C574F]">{it.name} ×{it.qty}{it.size ? ` · ${t("checkout.size")} ${it.size}` : ""}</span><span className="font-medium">{money(it.unit_price * it.qty, currency)}</span></div>
          ))}
          <div className="border-t border-[#F0ece4] mt-2 pt-2 flex justify-between text-sm"><span>{t("tracking.subtotal")}</span><span className="font-semibold">{money(order.subtotal, currency)}</span></div>
          {order.delivery_fee > 0 && <div className="flex justify-between text-sm"><span>{t("tracking.delivery")}</span><span className="font-semibold">{money(order.delivery_fee, currency)}</span></div>}
          {(order.tax_lines || []).map((t2) => (
            <div key={t2.name} className="flex justify-between text-sm text-[#5C574F]"><span>{t2.name} ({+(t2.rate * 100).toFixed(3)}%)</span><span>{money(t2.amount, currency)}</span></div>
          ))}
          <div className="flex justify-between font-display font-bold text-[#E85D04] mt-1"><span>{t("tracking.total")}</span><span>{money(order.total, currency)}</span></div>
          <a href={`${API}/public/orders/${order.order_id}/invoice.pdf`} target="_blank" rel="noreferrer" data-testid="download-invoice-btn" className="mt-3 w-full h-11 rounded-xl bg-[#F9F6F0] text-[#5C574F] font-semibold text-sm flex items-center justify-center gap-2">
            <FileDown className="h-4 w-4" /> {t("tracking.downloadInvoice")}
          </a>
        </div>

        {order.status === "a_valider" && (
          order.paid && order.payment_type === "online" ? (
            <p className="text-center text-sm font-semibold text-[#2D6A4F] bg-[#2D6A4F]/12 rounded-xl p-4" data-testid="awaiting-validation">
              {t("tracking.paidAwaiting")}
            </p>
          ) : (
            <p className="text-center text-sm text-[#5C574F] bg-[#FFBA08]/15 rounded-xl p-4" data-testid="awaiting-validation">
              {(order.delivery_fee > 0 || order.mode === "retrait") ? t("tracking.awaitingSimple") : t("tracking.awaiting")}
            </p>
          )
        )}

        {order.tracking_number && (
          <div className="rounded-xl border border-[#0077B6]/25 bg-[#0077B6]/5 p-4" data-testid="customer-tracking">
            <p className="text-sm font-semibold flex items-center gap-2 text-[#025b8c]"><Truck className="h-4 w-4" /> {t("tracking.shippedTitle")}</p>
            <p className="text-sm mt-1 text-[#5C574F]">{t("tracking.trackingNumber")} <span className="font-mono font-semibold text-[#1A1A1A]">{order.tracking_number}</span></p>
            {order.tracking_url && (
              <a href={order.tracking_url} target="_blank" rel="noreferrer" data-testid="customer-tracking-link" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0077B6] hover:underline">
                <ExternalLink className="h-4 w-4" /> {t("tracking.trackParcel")}
              </a>
            )}
          </div>
        )}

        {order.status === "en_livraison" && order.payment_type === "on_delivery" && (
          <p className="text-center text-sm text-[#5C574F] bg-[#E85D04]/10 rounded-xl p-4" data-testid="cod-info">
            {t("tracking.cod", { amount: money(order.total, currency) })}
          </p>
        )}

        {order.status === "en_attente_retrait" && (
          <div className="text-center text-sm text-[#5C574F] bg-[#7048E8]/10 rounded-xl p-4" data-testid="pickup-info">
            {t("tracking.pickupReady", { amount: money(order.total, currency) })}
            {order.slots?.length > 0 && <p className="mt-1">{t("tracking.slot", { slots: order.slots.map((s) => `${s.date} (${s.time})`).join(" / ") })}</p>}
          </div>
        )}

        {order.status === "a_payer" && !order.paid && (
          order.payment_type === "interac_etransfer" ? (
            <div className="rounded-xl border-2 border-[#E85D04]/40 bg-[#E85D04]/5 p-4 space-y-2" data-testid="interac-instructions">
              <p className="text-sm font-bold flex items-center gap-2 text-[#E85D04]"><CreditCard className="h-4 w-4" /> {t("tracking.interacTitle")}</p>
              <div className="bg-white rounded-lg p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.interacAmount")}</span><span className="font-bold text-[#1A1A1A]">{money(order.total, currency)}</span></div>
                <div className="flex justify-between gap-2"><span className="text-[#5C574F]">{t("tracking.interacSendTo")}</span><span className="font-semibold text-[#1A1A1A] break-all text-right" data-testid="interac-handle">{order.business_interac || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.interacRef")}</span><span className="font-mono font-semibold text-[#1A1A1A]">#{order.order_id.slice(-6)}</span></div>
              </div>
              <p className="text-xs text-[#9c968a]">{t("tracking.interacNote")}</p>
            </div>
          ) : order.payment_claim ? (
            <div className="text-center text-sm text-[#5C574F] bg-[#FFBA08]/15 rounded-xl p-4" data-testid="payment-claim-pending">
              {t("tracking.awaitingSellerConfirm")}
            </div>
          ) : (
            <PaymentPanel order={order} onPaid={() => qc.invalidateQueries({ queryKey: ["order", orderId] })} />
          )
        )}

        {order.business_whatsapp && (
          <a href={`https://wa.me/${order.business_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="mt-4 w-full h-12 rounded-2xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2" data-testid="contact-seller-btn">
            <Phone className="h-5 w-5" /> {t("tracking.contactSeller")}
          </a>
        )}
      </div>
      {order.business_slug && (
        <CustomerAssistant slug={order.business_slug} businessName={order.business_name} whatsapp={order.business_whatsapp} orderId={order.order_id} />
      )}

      {showPaySuccess && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowPaySuccess(false)}>
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
            <PaymentSuccess order={order} onContinue={() => setShowPaySuccess(false)} />
          </motion.div>
        </div>
      )}
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-[#5C574F] px-6 text-center">{children}</div>;
}
